import { useState } from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Spinner,
} from 'reactstrap';

/**
 * Modal component for confirming Snow/Ice Removal Log deletion
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls modal visibility
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Function} props.onDelete - Async callback to handle log deletion
 * @param {Object} props.snowLog - Snow/Ice Removal Log object to be deleted
 * @returns {JSX.Element} Modal for confirming snow log deletion
 */
const DeleteSnowLogConfirmationModal = ({
    open,
    onClose,
    onDelete,
    snowLog,
}) => {
    const [loading, setLoading] = useState(false);

    /**
     * Handles the confirmation of snow log deletion
     */
    const handleConfirm = async () => {
        try {
            setLoading(true);
            if (onDelete) {
                await onDelete();
            }
            onClose();
        } catch (error) {
            console.error('Delete failed:', error);
        } finally {
            setLoading(false);
        }
    };

    // Format the log description for better readability
    const logDate = snowLog?.date
        ? new Date(snowLog.date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
          })
        : 'this log';

    const buildingName = snowLog?.property_details?.building_name || 'unknown property';

    return (
        <Modal isOpen={open} toggle={onClose} centered className="snow-log-dialog">
            <ModalHeader toggle={onClose}>Confirm Deletion</ModalHeader>
            <ModalBody>
                <p className="mb-0">
                    Are you sure you want to delete the Snow/Ice Removal Log for{' '}
                    <strong>{buildingName}</strong> on <strong>{logDate}</strong>?
                    <br />
                    <small className="text-muted">
                        This action cannot be undone.
                    </small>
                </p>
            </ModalBody>
            <ModalFooter>
                <Button
                    outline
                    color="secondary"
                    onClick={onClose}
                    disabled={loading}
                >
                    Cancel
                </Button>
                <Button
                    color="danger"
                    onClick={handleConfirm}
                    disabled={loading}
                >
                    {loading && <Spinner size="sm" className="me-2" />}
                    {loading ? 'Deleting...' : 'Delete Log'}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export { DeleteSnowLogConfirmationModal };